import React, { useState, useEffect, useCallback } from 'react';
import { siteData } from '../data/mock';
import { Menu, X, ArrowUpRight } from 'lucide-react';

const AnimatedLogo = ({ size = 'default' }) => {
  const [slid, setSlid] = useState(false);

  useEffect(() => {
    let ticking = false;
    const THRESHOLD = 120;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const y = window.scrollY;
          const segment = Math.floor(y / THRESHOLD);
          const shouldSlide = segment % 2 === 1;
          if (shouldSlide !== slid) {
            setSlid(shouldSlide);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [slid]);

  const fs = size === 'large' ? '32px' : '28px';
  const slashFs = size === 'large' ? '26px' : '24px';
  const charStyle = { fontWeight: 700, fontSize: fs, fontFamily: "'Inter', sans-serif", lineHeight: 1 };

  return (
    <div className={`oa-logo-anim ${slid ? 'slid' : ''}`} style={{ fontSize: fs }}>
      {/* First position: O ↔ A */}
      <div className="oa-logo-char">
        <div className="oa-logo-char-a">
          <span style={{ ...charStyle, color: 'var(--brand-primary)' }}>O</span>
        </div>
        <div className="oa-logo-char-b">
          <span style={{ ...charStyle, color: 'var(--brand-primary)' }}>A</span>
        </div>
      </div>

      {/* Slash */}
      <span className="oa-logo-slash" style={{ fontWeight: 300, fontSize: slashFs, color: 'rgba(0, 255, 209, 0.3)', fontFamily: "'Inter', sans-serif", lineHeight: 1 }}>/</span>

      {/* Second position: A ↔ I */}
      <div className="oa-logo-char">
        <div className="oa-logo-char-a">
          <span style={{ ...charStyle, color: 'var(--text-primary)' }}>A</span>
        </div>
        <div className="oa-logo-char-b">
          <span style={{ ...charStyle, color: 'var(--text-primary)' }}>I</span>
        </div>
      </div>
    </div>
  );
};

export { AnimatedLogo };

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href === '#top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <header
        className="fixed top-0 w-full z-50 transition-all duration-500"
        style={{
          background: scrolled ? 'rgba(0,0,0,0.95)' : 'rgba(0,0,0,0.8)',
          backdropFilter: scrolled ? 'blur(16px)' : 'blur(8px)',
          borderBottom: `1px solid ${scrolled ? 'rgba(255,255,255,0.1)' : 'transparent'}`,
          height: '80px',
        }}
      >
        <div className="h-full flex items-center justify-between" style={{ padding: '0 7.6923%' }}>
          {/* Logo */}
          <a
            href="#top"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="oa-logo-container"
            style={{ textDecoration: 'none' }}
          >
            <AnimatedLogo />
            <div className="oa-logo-name hidden sm:block">
              <span className="oa-name-accent">Operational</span>
              <br />
              Alpha
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {siteData.nav.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(item.href); }}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-300"
                style={{ fontSize: '16px', fontWeight: 400, textDecoration: 'none', fontFamily: "'Inter', sans-serif" }}
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick('#contact'); }}
              className="oa-btn-primary"
              style={{ minHeight: '44px', padding: '10px 20px', fontSize: '15px' }}
            >
              Get in Touch
              <ArrowUpRight size={16} />
            </a>
          </nav>

          {/* Mobile Toggle */}
          <button
            className="md:hidden flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 flex flex-col items-start justify-center"
          style={{ background: 'rgba(0,0,0,0.98)', padding: '0 7.6923%' }}
        >
          <nav className="flex flex-col gap-6">
            {siteData.nav.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(item.href); }}
                className="oa-display-medium text-[var(--text-primary)] hover:text-[var(--brand-primary)] transition-colors duration-300"
                style={{ textDecoration: 'none' }}
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick('#contact'); }}
              className="oa-btn-primary mt-4"
            >
              Get in Touch
              <ArrowUpRight size={18} />
            </a>
          </nav>
        </div>
      )}
    </>
  );
};
